import { type ReactNode } from "react";
import {
  traceLabelGlyph,
  traceLabelText,
  traceLabelTitle,
  traceLabels,
  TRACE_LABEL_ORDER,
  type TraceLabelState,
} from "./vocab.ts";
import type { TraceSummary } from "./use-traces.ts";

/**
 * The two INDEPENDENT trace labels — `inferred` and `partial` — rendered for the trace LIST (task
 * 11.11; design 7.4 / 12.2). This mirrors, in the list, exactly the rule the shared `Waterfall`
 * component applies in the detail view: the two flags are read SEPARATELY and each present flag
 * renders its OWN badge.
 *
 * The named, testable requirement lives here for the list, and nowhere are the two flags coupled:
 *   - neither true  → no badge;
 *   - only inferred → the inferred badge alone;
 *   - only partial  → the partial badge alone;
 *   - BOTH true     → BOTH badges, side by side.
 * NEITHER takes precedence: one is never hidden when the other is present, they are not merged into
 * a single combined label, and the fixed reading order ({@link TRACE_LABEL_ORDER}) is for
 * consistency only — it confers no dominance. Each badge is icon+text in a neutral style (never a
 * status colour), so a label never reads as a health state (design 12.4).
 */
export function TraceLabels(props: {
  /** The trace whose two independent flags to render. */
  readonly trace: Pick<TraceSummary, "inferred" | "partial">;
  /** An accessible prefix for each badge's label, e.g. the trace id, so screen readers disambiguate. */
  readonly context?: string;
}): ReactNode {
  const labels: TraceLabelState = traceLabels(props.trace);

  // Each flag is tested on its OWN — no flag gates another, so all four combinations render faithfully.
  const present = TRACE_LABEL_ORDER.filter((kind) => labels[kind]);

  if (present.length === 0) {
    return (
      <span className="trc-labels" data-empty="true">
        <span className="trc-labels__none" title="No inferred or partial label on this trace.">
          n/a
        </span>
      </span>
    );
  }

  return (
    <span className="trc-labels">
      {present.map((kind) => (
        <TraceLabelBadge key={kind} kind={kind} context={props.context} />
      ))}
    </span>
  );
}

/**
 * One independent label badge (icon + text), in the same neutral visual language the `Waterfall`
 * uses for its trace labels. The `title` carries the full phrase; an extra screen-reader string
 * folds in the context (the trace id) so each badge is unambiguous when a row has both.
 */
function TraceLabelBadge(props: {
  readonly kind: keyof TraceLabelState;
  // `| undefined` so a caller may pass an optional context straight through under
  // exactOptionalPropertyTypes (the badge already guards `context !== undefined`).
  readonly context?: string | undefined;
}): ReactNode {
  const text = traceLabelText(props.kind);
  const title = traceLabelTitle(props.kind);
  return (
    <span className="trace-label" data-kind={props.kind} title={title}>
      <span className="trace-label__glyph" aria-hidden="true">
        {traceLabelGlyph(props.kind)}
      </span>
      <span>{text}</span>
      {props.context !== undefined && (
        <span className="sr-only">
          {` ${text} label on trace ${props.context}. ${title}`}
        </span>
      )}
    </span>
  );
}
